import React, { useState, useRef, useEffect } from 'react';
import { InputGroup } from '@blueprintjs/core';
import { useField, useFormikContext } from 'formik';
import { NBSP, fmtNumber, parseNumber } from '@/utils/format';

interface DecimalInputProps {
  name?: string;
  value?: number | string;
  onChange?: (value: number | null) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  fractionDigits?: number;
  maxFractionDigits?: number;
  min?: number;
  max?: number;
  /** Group thousands when displaying (default: true). Disable for compact
   *  fields like exchange rates. */
  useGrouping?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  rightElement?: React.ReactNode;
  leftElement?: React.ReactNode;
  fill?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
}

/** Nombre de décimales réellement portées par la valeur (« 1,1 » -> 1). */
const decimalsOf = (n: number): number => {
  const s = String(n);
  if (s.includes('e') || s.includes('E')) return 0;
  return (s.split('.')[1] || '').length;
};

/**
 * Affichage du champ dans la convention UNIQUE du produit (espace insécable +
 * virgule décimale), quelle que soit la locale du navigateur.
 *
 * `Intl.NumberFormat(locale, …)` rendait « 1,234.56 » sur un poste anglais et
 * « 1 234,56 » sur un poste français : le même champ montait deux montants
 * différents selon la machine, alors que TOUTES les grilles affichent la
 * convention française. La saisie, elle, reste tolérante — `parseNumber`
 * accepte indifféremment la virgule et le point.
 *
 * `fractionDigits` / `maxFractionDigits` gardent la sémantique min/max d'`Intl` :
 * au moins `fractionDigits` décimales, au plus `maxFractionDigits`.
 */
const formatForDisplay = (
  n: number | null | undefined,
  fractionDigits?: number,
  maxFractionDigits?: number,
  useGrouping: boolean = true,
): string => {
  if (n === null || n === undefined || Number.isNaN(n)) return '';
  const min = fractionDigits ?? 0;
  const max = Math.max(min, maxFractionDigits ?? fractionDigits ?? 20);
  const digits = Math.min(Math.max(decimalsOf(Number(n)), min), max);
  const formatted = fmtNumber(Number(n), digits);

  return useGrouping ? formatted : formatted.split(NBSP).join('');
};

/**
 * A plain decimal text input. Accepts both `,` and `.` as decimal separator,
 * does not reformat while the user is typing (no cursor jumps, no zero-pad
 * surprises), and only commits the parsed numeric value to the parent on blur.
 */
export const DecimalInput = React.forwardRef<
  HTMLInputElement,
  DecimalInputProps
>(function DecimalInput(
  {
    value,
    onChange,
    onBlur,
    onFocus,
    fractionDigits,
    maxFractionDigits,
    useGrouping = true,
    min,
    max,
    readOnly,
    ...rest
  }: any,
  ref: any,
) {
  const [isFocused, setIsFocused] = useState(false);
  const [draft, setDraft] = useState<string>(() =>
    formatForDisplay(
      typeof value === 'string' ? parseNumber(value) : value,
      fractionDigits,
      maxFractionDigits,
      useGrouping,
    ),
  );

  const lastExternalValueRef = useRef(value);
  useEffect(() => {
    if (isFocused) return;
    if (lastExternalValueRef.current === value) return;
    lastExternalValueRef.current = value;
    const numeric = typeof value === 'string' ? parseNumber(value) : value;
    setDraft(
      formatForDisplay(numeric, fractionDigits, maxFractionDigits, useGrouping),
    );
  }, [value, fractionDigits, maxFractionDigits, useGrouping, isFocused]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Allow only digits, one decimal mark (`,` or `.`), and a leading `-`.
    if (!/^-?[0-9]*[.,]?[0-9]*$/.test(raw) && raw !== '' && raw !== '-') {
      return;
    }
    setDraft(raw);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    // Drop grouping while typing so cursor placement stays predictable —
    // "1 500,00" with NBSPs is a pain to edit mid-string.
    if (useGrouping) {
      const numeric = typeof value === 'string' ? parseNumber(value) : value;
      setDraft(
        formatForDisplay(numeric, fractionDigits, maxFractionDigits, false),
      );
    }
    onFocus && onFocus(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    const parsed = parseNumber(draft);
    let next = parsed;
    if (next !== null) {
      if (typeof min === 'number' && next < min) next = min;
      if (typeof max === 'number' && next > max) next = max;
    }
    setDraft(
      formatForDisplay(next, fractionDigits, maxFractionDigits, useGrouping),
    );
    lastExternalValueRef.current = next;
    onChange && onChange(next);
    onBlur && onBlur(e);
  };

  return (
    <InputGroup
      type="text"
      inputMode="decimal"
      readOnly={readOnly}
      {...rest}
      value={draft}
      onChange={readOnly ? undefined : handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      inputRef={ref as any}
    />
  );
});

interface FDecimalInputProps extends Omit<
  DecimalInputProps,
  'value' | 'onChange'
> {
  name: string;
}

/** Formik-bound variant. Stores the parsed number (or null) in formik state. */
export const FDecimalInput = ({
  name,
  onBlur,
  ...rest
}: FDecimalInputProps) => {
  const [field] = useField<number | null | undefined>(name);
  const { setFieldValue, setFieldTouched } = useFormikContext();

  return (
    <DecimalInput
      {...rest}
      name={name}
      value={field.value as any}
      onChange={(v: any) => setFieldValue(name, v, true)}
      onBlur={(e: any) => {
        setFieldTouched(name, true);
        onBlur && onBlur(e);
      }}
    />
  );
};
